import React, { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { MessageSquare, Send, Award, X } from 'lucide-react';
import api from '../../utils/axios';
import { toast } from 'sonner';

export const CasoCommentsSection = ({
  caso, newComment, setNewComment, addingComment, onAddComment, onCaseUpdate,
}) => {
  const [settingConclusion, setSettingConclusion] = useState(null);

  const handleSetConclusion = async (commentId) => {
    setSettingConclusion(commentId);
    try {
      const res = await api.post(`/cases/${caso.id}/set-conclusion`, { comment_id: commentId });
      if (onCaseUpdate) onCaseUpdate(res.data);
      toast.success('Marcado como conclusión del caso');
    } catch {
      toast.error('Error al marcar conclusión');
    } finally {
      setSettingConclusion(null);
    }
  };

  const handleRemoveConclusion = async () => {
    setSettingConclusion('removing');
    try {
      const res = await api.delete(`/cases/${caso.id}/remove-conclusion`);
      if (onCaseUpdate) onCaseUpdate(res.data);
      toast.success('Conclusión removida');
    } catch {
      toast.error('Error al remover conclusión');
    } finally {
      setSettingConclusion(null);
    }
  };

  const conclusionId = caso.conclusion_comment_id;

  return (
    <Card className="p-4 bg-white lg:col-span-2">
      <h3 className="font-semibold flex items-center gap-2 mb-3">
        <MessageSquare size={16} className="text-purple-600" />
        Comentarios
      </h3>

      {/* Conclusion banner if exists */}
      {caso.conclusion && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="conclusion-banner">
          <div className="flex items-start gap-2">
            <Award size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Conclusión del Caso</p>
              <p className="text-sm text-slate-800">{caso.conclusion}</p>
            </div>
            <button
              onClick={handleRemoveConclusion}
              disabled={settingConclusion === 'removing'}
              className="text-amber-400 hover:text-amber-600 shrink-0 p-0.5"
              title="Quitar conclusión"
              data-testid="remove-conclusion-btn"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
        {caso.comentarios?.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">Sin comentarios</p>
        ) : (
          caso.comentarios?.map((com, idx) => {
            const isConclusion = com.id === conclusionId || com.es_conclusion;
            return (
              <div
                key={com.id || idx}
                className={`p-3 rounded-lg group relative ${
                  isConclusion
                    ? 'bg-amber-50 border-2 border-amber-300 ring-1 ring-amber-200'
                    : 'bg-slate-50'
                }`}
                data-testid={`comment-${com.id || idx}`}
              >
                {isConclusion && (
                  <div className="flex items-center gap-1 mb-1">
                    <Award size={12} className="text-amber-600" />
                    <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Conclusión</span>
                  </div>
                )}
                <p className="text-sm text-slate-800">{com.texto}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-500">{com.usuario} - {new Date(com.fecha).toLocaleString()}</p>
                  {!isConclusion && (
                    <button
                      onClick={() => handleSetConclusion(com.id)}
                      disabled={!!settingConclusion}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-amber-50"
                      title="Marcar como conclusión del caso"
                      data-testid={`set-conclusion-${com.id || idx}`}
                    >
                      <Award size={11} />
                      Conclusión
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Agregar comentario..." value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && onAddComment()}
          style={{ backgroundColor: '#fff' }}
          data-testid="comment-input"
        />
        <button onClick={onAddComment} disabled={addingComment || !newComment.trim()}
          className="px-3 py-2 rounded-md text-white disabled:opacity-50"
          style={{ backgroundColor: '#7c3aed' }}
          data-testid="comment-submit-btn"
        >
          <Send size={16} />
        </button>
      </div>
    </Card>
  );
};

export const CasoInfoPanel = ({ caso }) => (
  <Card className="p-4 bg-white border-slate-200">
    <h3 className="font-semibold mb-3 text-slate-700">Información del Caso</h3>
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div><Label className="text-xs text-slate-500">Creado por</Label><p className="text-slate-800">{caso.creado_por}</p></div>
      <div><Label className="text-xs text-slate-500">Fecha creación</Label>
        <p className="text-slate-800">{caso.fecha_creacion || caso.created_at ? new Date(caso.fecha_creacion || caso.created_at).toLocaleString() : 'N/A'}</p></div>
      <div><Label className="text-xs text-slate-500">Última actualización</Label>
        <p className="text-slate-800">{caso.fecha_actualizacion || caso.updated_at ? new Date(caso.fecha_actualizacion || caso.updated_at).toLocaleString() : 'N/A'}</p></div>
      {caso.tags?.length > 0 && (
        <div><Label className="text-xs text-slate-500">Tags</Label>
          <div className="flex flex-wrap gap-1 mt-1">{caso.tags.map((tag, idx) => <Badge key={idx} variant="outline" className="text-xs">{tag}</Badge>)}</div></div>
      )}
    </div>
  </Card>
);
